import { useState } from 'react';
import HologramCard from '../../../components/base/HologramCard';
import GlitchText from '../../../components/base/GlitchText';

export default function PortfolioSection() {
  const [selectedCategory, setSelectedCategory] = useState('prds');

  const portfolioItems = {
    prds: {
      title: 'PRODUCT REQUIREMENTS',
      icon: 'ri-file-text-line',
      color: 'purple',
      items: [
        {
          name: 'Feature Specification Documents',
          description: 'Comprehensive PRDs for new product features',
          type: 'PRD',
          status: 'ACTIVE'
        },
        {
          name: 'User Story Mapping',
          description: 'Detailed user journey and story documentation',
          type: 'MAPPING',
          status: 'UPDATED'
        },
        {
          name: 'Technical Requirements',
          description: 'API specifications and technical constraints',
          type: 'TECH',
          status: 'REVIEW'
        }
      ]
    },
    research: {
      title: 'USER RESEARCH',
      icon: 'ri-user-search-line',
      color: 'cyan',
      items: [
        {
          name: 'User Interview Summaries',
          description: 'Insights from 50+ user interviews',
          type: 'INTERVIEW',
          status: 'COMPLETE'
        },
        {
          name: 'Usability Testing Reports',
          description: 'A/B testing results and recommendations',
          type: 'TESTING',
          status: 'ACTIVE'
        },
        {
          name: 'Persona Development',
          description: 'Data-driven user persona creation',
          type: 'PERSONA',
          status: 'VALIDATED'
        }
      ]
    },
    analysis: {
      title: 'COMPETITIVE ANALYSIS',
      icon: 'ri-bar-chart-line',
      color: 'green',
      items: [
        {
          name: 'Market Research Reports',
          description: 'Comprehensive competitor analysis',
          type: 'MARKET',
          status: 'CURRENT'
        },
        {
          name: 'Feature Comparison Matrix',
          description: 'Side-by-side feature analysis',
          type: 'MATRIX',
          status: 'UPDATED'
        },
        {
          name: 'Pricing Strategy Analysis',
          description: 'Market positioning and pricing insights',
          type: 'PRICING',
          status: 'DRAFT'
        }
      ]
    },
    ux: {
      title: 'UX EVALUATIONS',
      icon: 'ri-palette-line',
      color: 'orange',
      items: [
        {
          name: 'Heuristic Evaluations',
          description: 'UI/UX audit and improvement recommendations',
          type: 'HEURISTIC',
          status: 'COMPLETE'
        },
        {
          name: 'Accessibility Audits',
          description: 'WCAG compliance and accessibility improvements',
          type: 'A11Y',
          status: 'ONGOING'
        },
        {
          name: 'Design System Documentation',
          description: 'Component library and design guidelines',
          type: 'DESIGN',
          status: 'ACTIVE'
        }
      ]
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': case 'CURRENT': return 'green';
      case 'COMPLETE': case 'VALIDATED': return 'cyan';
      case 'UPDATED': case 'ONGOING': return 'orange';
      case 'REVIEW': case 'DRAFT': return 'purple';
      default: return 'gray';
    }
  };

  return (
    <section id="portfolio" className="min-h-screen py-20 bg-gradient-to-b from-gray-900 to-black">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-white font-orbitron mb-4">
            <GlitchText text="MOTHERBOARD" />
          </h2>
          <div className="text-xl text-purple-400 font-mono mb-2">CENTRAL PROCESSING BOARD</div>
          <p className="text-gray-400 font-rajdhani text-lg">Core documentation and strategic artifacts</p>
        </div>

        {/* Portfolio Drive Link */}
        <div className="mb-12">
          <HologramCard glowColor="cyan" className="text-center">
            <div className="space-y-4">
              <i className="ri-cloud-line text-4xl text-cyan-400"></i>
              <h3 className="text-xl font-bold text-white font-orbitron">PORTFOLIO DRIVE ACCESS</h3>
              <p className="text-gray-400 font-rajdhani">
                Complete collection of PRDs, case studies, and documentation
              </p>
              <a 
                href="https://drive.google.com/drive/folders/1LkXNcQG6-a-yfMNE5fg1mT744myifdjJ?usp=drive_link"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 px-6 py-3 bg-cyan-500/20 text-cyan-400 border border-cyan-500/50 rounded-lg hover:bg-cyan-500/30 transition-all duration-300 font-mono"
              >
                <i className="ri-external-link-line"></i>
                <span>ACCESS DRIVE</span>
              </a>
            </div>
          </HologramCard>
        </div>

        {/* Category Selector */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {Object.entries(portfolioItems).map(([key, category]) => (
            <button
              key={key}
              onClick={() => setSelectedCategory(key)}
              className={`
                px-6 py-3 rounded-lg font-mono text-sm transition-all duration-300
                flex items-center space-x-2 border-2
                ${selectedCategory === key 
                  ? `bg-${category.color}-500/20 text-${category.color}-400 border-${category.color}-500/50 shadow-lg shadow-${category.color}-500/30` 
                  : 'text-gray-300 border-gray-600 hover:border-gray-400'
                }
              `}
            >
              <i className={`${category.icon} text-lg`}></i>
              <span>{category.title}</span>
            </button>
          ))}
        </div>

        {/* Portfolio Items */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {portfolioItems[selectedCategory as keyof typeof portfolioItems].items.map((item, index) => (
            <HologramCard 
              key={item.name}
              glowColor={portfolioItems[selectedCategory as keyof typeof portfolioItems].color}
              className="animate-fade-in"
              style={{ animationDelay: `${index * 0.1}s` } as React.CSSProperties}
            >
              <div className="space-y-4">
                {/* Item Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <i className={`${portfolioItems[selectedCategory as keyof typeof portfolioItems].icon} text-xl text-${portfolioItems[selectedCategory as keyof typeof portfolioItems].color}-400`}></i>
                    <div>
                      <h3 className="text-lg font-bold text-white font-orbitron">{item.name}</h3>
                      <div className="text-xs text-gray-400 font-mono">{item.type}</div>
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded text-xs font-mono bg-${getStatusColor(item.status)}-500/20 text-${getStatusColor(item.status)}-400`}>
                    {item.status}
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-300 font-rajdhani text-sm leading-relaxed">
                  {item.description}
                </p>

                {/* File System Info */}
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center space-x-2 text-gray-500">
                    <i className="ri-file-line"></i>
                    <span className="font-mono">DOCUMENT</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-500">
                    <i className="ri-time-line"></i>
                    <span className="font-mono">RECENT</span>
                  </div>
                </div>

                {/* Access Indicator */}
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-xs text-green-400 font-mono">ACCESSIBLE</span>
                </div>
              </div>
            </HologramCard>
          ))}
        </div>
      </div>
    </section>
  );
}
